import { useEffect, useState } from 'react';
import { useThemeContext, useUserContext } from 'contexts';
import { getUserViewedListings } from 'pages/api/viewed-listings/viewedListingsApiClient';
import { getListingsByIds } from 'data/listing/go-search/fetcher';
import { AreaPageListingCardData } from 'components/pages/area-listings/area_page_listing_card_data';
import { isDeleted } from 'types/reso';
import type { ListingCardData } from 'components/listing-card';
import type { GetViewedListingsResponse } from 'pages/api/viewed-listings/types';

const NO_LISTINGS: readonly ListingCardData[] = [];

/**
 * How many of the most recently viewed listings the tab shows. Kept local
 * rather than shared with the Favourites page size: the two happen to match,
 * but nothing requires them to.
 */
const HISTORY_LIMIT = 36;

export type UseCollectionsHistoryResult = {
  listings: readonly ListingCardData[];
  isLoading: boolean;
  /** True when the fetch failed, as opposed to returning nothing. */
  hasError: boolean;
};

/**
 * The signed-in user's recently-viewed listings as listing-card data, most
 * recent first, capped at the {@link HISTORY_LIMIT} most recent. The
 * viewed-listings service returns ids only and paginates nowhere in the chain,
 * so the newest ids are taken and hydrated in one `getListingsByIds` call.
 *
 * Listings go-search reports as deleted are left out: their card shows a
 * "Delete" status badge, which is an internal state rather than anything a
 * user would recognise.
 *
 * `isActive` gates the fetch so the Collections pane only loads the tab being
 * looked at. A failure is reported as `hasError` rather than an empty list, so
 * the pane can say the fetch failed instead of claiming there is no history.
 */
export function useCollectionsHistory(isActive: boolean): UseCollectionsHistoryResult {
  const { isAuthenticated, user } = useUserContext();
  const { themeName } = useThemeContext();
  // `null` until the first fetch settles, which is what drives `isLoading`.
  const [loaded, setLoaded] = useState<readonly ListingCardData[] | null>(null);
  const [hasError, setHasError] = useState(false);
  const userId = user?.id;
  const canLoad = isActive && isAuthenticated && Boolean(userId);

  useEffect(() => {
    if (!canLoad || !userId) {
      return undefined;
    }

    let cancelled = false;

    // Only a retry of a failed fetch discards what's on screen, so the error
    // can't linger through its own refetch; a list that loaded fine stays
    // rendered while it refreshes.
    if (hasError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoaded(null);
      setHasError(false);
    }

    const load = async () => {
      const response = await getUserViewedListings({ userId, sourceTheme: themeName }) as GetViewedListingsResponse;
      // The service hands back newest-viewed first, so the cap is the head of
      // the list. Applied before hydrating, so only what is shown is fetched.
      const viewedIds = (response?.viewedListings?.map(viewed => viewed.listingId) ?? []).slice(0, HISTORY_LIMIT);
      if (viewedIds.length === 0) {
        return NO_LISTINGS;
      }

      // go-search returns matches in its own order, so restore view order (the
      // service already hands back newest-viewed first) before building cards.
      const hydrated = await getListingsByIds(viewedIds);
      const viewOrder = new Map(viewedIds.map((id, index) => [id, index]));
      return [...hydrated]
        .filter(listing => !isDeleted(listing.standardStatus))
        .sort((a, b) => (viewOrder.get(a.id) ?? 0) - (viewOrder.get(b.id) ?? 0))
        .map(listing => new AreaPageListingCardData(listing));
    };

    load()
      .then(listings => {
        if (cancelled) return;
        setHasError(false);
        setLoaded(listings);
      })
      .catch(() => {
        if (cancelled) return;
        setHasError(true);
        setLoaded(NO_LISTINGS);
      });

    return () => {
      cancelled = true;
    };
  // `hasError` is read but deliberately not a dependency: it should only be
  // consulted when a new request starts. Depending on it would make a failure
  // trigger its own refetch, and keep doing so for as long as it kept failing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad, userId, themeName]);

  return {
    listings: loaded ?? NO_LISTINGS,
    isLoading: canLoad && loaded === null,
    hasError,
  };
}
